import { Link } from 'react-router';
import { COMPANY } from './company';
import { Bullets, Email, LegalPage, Term, type LegalSection } from './LegalPage';

/**
 * The terms of service at my.stratum.ie/terms.
 *
 * <p>
 * DRAFT TEXT for a business-to-business service. Where a customer has signed an
 * order form or a separate agreement with Stratum, that document wins (section
 * 1 says so) — these terms are the baseline, not a replacement for it. Not yet
 * reviewed by a lawyer.
 * </p>
 */
const SECTIONS: LegalSection[] = [
  {
    id: 'agreement',
    heading: 'About these terms',
    body: (
      <>
        <p>
          These terms govern the use of {COMPANY.product} — the myTMS, myDepot, myCheck and
          mySubsistence web apps, the myDriver mobile app and related services (the “Service”) —
          provided by {COMPANY.legalName}, trading as {COMPANY.name} (“{COMPANY.name}”, “we”,
          “us”).
        </p>
        <p>
          They are an agreement between {COMPANY.name} and the business that subscribes to the
          Service (the “Customer”). Anyone using the Service on the Customer’s behalf (a “User”)
          must follow them too. If the Customer has signed an order form or other written agreement
          with us, that agreement takes priority where it differs from these terms.
        </p>
        <p>
          The Service is for business use. By using it you confirm you are doing so for a business
          and are authorised to accept these terms for it.
        </p>
      </>
    ),
  },
  {
    id: 'accounts',
    heading: 'Accounts and users',
    body: (
      <Bullets
        items={[
          'The Customer decides who its Users are and what each can see and do, and is responsible for everything done under its account.',
          'Users must keep their sign-in details to themselves, and tell us promptly about any unauthorised use.',
          'Drivers signing in to myDriver with a phone number are Users of the Customer that invited them.',
          `With the Customer’s permission, ${COMPANY.name} support staff may access its account to help resolve an issue. Changes made during these visits appear in the account’s audit trail.`,
        ]}
      />
    ),
  },
  {
    id: 'subscriptions',
    heading: 'Subscriptions and fees',
    body: (
      <>
        <p>
          The Customer subscribes to the apps it chooses, at the prices and for the term agreed with
          us. Fees are invoiced as agreed and are payable within the time stated on the invoice.
          Fees exclude VAT and other taxes, which are added where they apply.
        </p>
        <p>
          We may change prices on renewal, with reasonable notice. If fees remain unpaid after a
          reminder, we may suspend access until they are paid.
        </p>
      </>
    ),
  },
  {
    id: 'acceptable-use',
    heading: 'Acceptable use',
    body: (
      <>
        <p>The Customer and its Users must not:</p>
        <Bullets
          items={[
            'use the Service for anything unlawful, fraudulent or harmful;',
            'upload material they have no right to use, or that contains malware;',
            'try to get around security, access other customers’ data, or probe or disrupt the Service;',
            'copy, resell or reverse-engineer the Service, or use it to build a competing product;',
            'use connected email to send unsolicited marketing, spam, or messages that break the law or the email provider’s rules.',
          ]}
        />
      </>
    ),
  },
  {
    id: 'customer-data',
    heading: 'Customer data',
    body: (
      <>
        <p>
          Everything the Customer and its Users put into the Service (“Customer Data”) remains the
          Customer’s. The Customer gives us permission to host, process and transmit Customer Data
          only as needed to provide and support the Service, and is responsible for having the
          right to put it there — including any personal data of its staff, drivers, customers and
          suppliers.
        </p>
        <p>
          We process personal data within Customer Data as the Customer’s processor, as described in
          our{' '}
          <Link to="/privacy" className="font-medium text-[#0089c3] hover:underline">
            privacy policy
          </Link>{' '}
          and, where the Customer requires one, in a data processing agreement with us.
        </p>
        <p>
          When a subscription ends, the Customer may export its data for 30 days, after which we
          delete it, except where the law requires us to keep it.
        </p>
      </>
    ),
  },
  {
    id: 'connected-services',
    heading: 'Connected services and email',
    body: (
      <>
        <p>
          The Customer may connect third-party services to {COMPANY.product} — for example a
          Microsoft 365 or Google Workspace mailbox, or a GPS or tachograph provider. When it does:
        </p>
        <Bullets
          items={[
            <Term key="auth" label="Authority">
              the User connecting the service confirms they are allowed to do so for the Customer,
              including permission to send email from the mailbox they connect.
            </Term>,
            <Term key="their" label="Their terms apply">
              the third party’s own terms and policies govern that service. We are not responsible
              for its availability, or for changes it makes that affect the connection.
            </Term>,
            <Term key="content" label="Content and recipients">
              the Customer is responsible for the emails it sends through the Service — their
              content, attachments and recipients.
            </Term>,
            <Term key="fallback" label="If a connection stops working">
              we will show that it needs reconnecting. Depending on the Customer’s settings, emails
              will either wait or be sent from our own address instead.
            </Term>,
          ]}
        />
      </>
    ),
  },
  {
    id: 'availability',
    heading: 'Availability and support',
    body: (
      <p>
        We work to keep the Service available and secure, and plan maintenance to minimise
        disruption. Support is available by email at <Email address={COMPANY.supportEmail} /> and by
        phone on {COMPANY.phone}, {COMPANY.supportHours}. We may improve and change the Service over time; we will not make a
        change that materially reduces the functionality a Customer has paid for during its current
        term.
      </p>
    ),
  },
  {
    id: 'calculations',
    heading: 'Calculations and estimates',
    body: (
      <p>
        The Service calculates things such as route distances, journey times, CO₂ estimates, tax and
        VAT treatments, charges and subsistence amounts, based on the data and settings the Customer
        provides and on third-party data. These are tools to help the Customer, not professional
        advice. The Customer remains responsible for checking its invoices, tax returns, regulatory
        records and compliance obligations.
      </p>
    ),
  },
  {
    id: 'ip',
    heading: 'Intellectual property',
    body: (
      <p>
        {COMPANY.name} owns the Service, its software and its content. The Customer receives a
        non-exclusive, non-transferable right for its Users to use the Service during its
        subscription. If the Customer sends us suggestions, we may use them freely to improve the
        Service.
      </p>
    ),
  },
  {
    id: 'confidentiality',
    heading: 'Confidentiality',
    body: (
      <p>
        Each party will keep the other’s confidential information confidential and use it only for
        the purposes of this agreement, except where it is already public, independently developed,
        or must be disclosed by law.
      </p>
    ),
  },
  {
    id: 'warranties',
    heading: 'Warranties and liability',
    body: (
      <>
        <p>
          We will provide the Service with reasonable skill and care. Beyond that, and to the extent
          the law allows, the Service is provided “as is”, without other warranties.
        </p>
        <p>
          Neither party is liable for indirect or consequential loss, or for loss of profit,
          revenue, goodwill or data that could have been avoided by reasonable backups. Each party’s
          total liability under this agreement in any 12-month period is limited to the fees the
          Customer paid in that period. Nothing in these terms limits liability that cannot be
          limited by law, such as for fraud or for death or personal injury caused by negligence.
        </p>
      </>
    ),
  },
  {
    id: 'termination',
    heading: 'Suspension and termination',
    body: (
      <>
        <p>
          Either party may end the subscription at the end of its term with the notice agreed, or
          immediately if the other seriously breaches these terms and does not put it right within
          30 days of being told.
        </p>
        <p>
          We may suspend access straight away if it is needed to protect the Service, other
          customers or the law — and will restore it as soon as the issue is resolved. Sections that
          by their nature should survive the end of the agreement (such as customer data,
          confidentiality and liability) do so.
        </p>
      </>
    ),
  },
  {
    id: 'changes',
    heading: 'Changes to these terms',
    body: (
      <p>
        We may update these terms. We will give Customers at least 30 days’ notice of changes that
        materially affect them; continuing to use the Service after that means accepting the new
        terms.
      </p>
    ),
  },
  {
    id: 'law',
    heading: 'Governing law',
    body: (
      <p>
        These terms are governed by the laws of {COMPANY.jurisdiction}, and the courts of{' '}
        {COMPANY.jurisdiction} have exclusive jurisdiction over any dispute arising from them.
      </p>
    ),
  },
  {
    id: 'contact',
    heading: 'Contact',
    body: (
      <p>
        {COMPANY.legalName}, trading as {COMPANY.name}, {COMPANY.registeredAddress}. Company number{' '}
        {COMPANY.companyNumber}, VAT number {COMPANY.vatNumber}. Email:{' '}
        <Email address={COMPANY.supportEmail} />, phone: {COMPANY.phone}.
      </p>
    ),
  },
];

export function TermsPage() {
  return (
    <LegalPage
      title="Terms of service"
      intro={
        <p className="text-base">
          These are the terms on which {COMPANY.name} provides {COMPANY.product} to businesses. We
          have kept them as short and plain as we can.
        </p>
      }
      sections={SECTIONS}
    />
  );
}
